import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { theme } from '../../styles/theme';
import { useWallet } from '@solana/wallet-adapter-react';
import { profile as apiProfile } from '../../lib/api';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';

export default function ProfileSettings({ open, onClose }) {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [profile, setProfile] = useState({
    username: '',
    email: '',
    notify_own_projects: false,
    notify_team_projects: false
  });

  // Load profile on mount
  useEffect(() => {
    if (open && walletAddress) {
      loadProfile();
    }
  }, [open, walletAddress]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await apiProfile.get(walletAddress);

      if (data) {
        setProfile({
          username: data.username || '',
          email: data.email || '',
          notify_own_projects: data.notify_own_projects || false,
          notify_team_projects: data.notify_team_projects || false
        });
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Validate email if provided
      if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
        setError('Please enter a valid email address');
        return;
      }

      // Upsert profile (creates or updates)
      await apiProfile.upsert(walletAddress, {
        username: profile.username || null,
        email: profile.email || null,
        notify_own_projects: profile.notify_own_projects,
        notify_team_projects: profile.notify_team_projects
      });

      setSuccess(true);
      
      // Dispatch event to notify other components that profile was updated
      window.dispatchEvent(new CustomEvent('profileSettingsUpdated'));
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={false}
      PaperProps={{
        sx: {
          bgcolor: theme.colors.background,
          backgroundImage: 'none',
          border: `1px solid ${theme.colors.border}`,
          m: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 64px)' },
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ color: theme.colors.primary }} />
          <Typography variant="h6">Profile Settings</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Profile settings saved successfully!
              </Alert>
            )}

            {/* Wallet Address */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Wallet Address
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: theme.colors.cardBackground,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${theme.colors.border}`,
                  wordBreak: 'break-all'
                }}
              >
                {walletAddress}
              </Typography>
            </Box>

            {/* Username */}
            <TextField
              fullWidth
              label="Username (Display Name)"
              value={profile.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="Enter a display name"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={profile.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="your.email@example.com"
              helperText="Required for email notifications"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Notification Preferences */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotificationsIcon sx={{ color: theme.colors.primary }} />
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  Email Notifications
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose when you want to receive email notifications for NFT generation jobs.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={profile.notify_own_projects}
                    onChange={(e) => handleChange('notify_own_projects', e.target.checked)}
                    disabled={!profile.email}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      My Own Projects
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get notified when your own generation jobs complete (server-side or client-side)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={profile.notify_team_projects}
                    onChange={(e) => handleChange('notify_team_projects', e.target.checked)}
                    disabled={!profile.email}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Team Projects
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get notified when team members complete generation jobs (server-side only)
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />

              {!profile.email && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please enter an email address to enable notifications
                </Alert>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.colors.border}` }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          sx={{
            background: `linear-gradient(135deg, ${theme.colors.primary} 0%, #ef4444 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, #b30000 0%, ${theme.colors.primary} 100%)`,
            }
          }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
